import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";

const cardSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: "0 10px 30px rgba(15, 76, 68, 0.055)",
};

const requestParameters = [
  {
    name: "to",
    type: "string",
    required: true,
    description:
      "Recipient phone number with country code. Formatting characters are removed; 8–15 digits are accepted.",
    example: "+91 98765 43210",
  },
  {
    name: "message",
    type: "object",
    required: true,
    description:
      "Message payload. Its fields depend on the selected message type.",
    example: '{ "type": "text", ... }',
  },
  {
    name: "replyTo",
    type: "string",
    required: false,
    description:
      "WhatsApp message ID to quote when sending this message as a reply.",
    example: "wamid.HBgM...",
  },
  {
    name: "clientReference",
    type: "string",
    required: false,
    description:
      "Your own correlation ID. It is returned in send and status responses.",
    example: "order-1048",
  },
];

const messageTypes = [
  {
    key: "text",
    label: "Text",
    description: "Send a plain-text message with optional URL previews.",
    body: {
      to: "919876543210",
      message: {
        type: "text",
        text: "Thanks for contacting us. How can we help?",
        previewUrl: false,
      },
      clientReference: "support-1048",
    },
    fields: [
      ["message.text", "string", "Yes", "Non-empty message body."],
      [
        "message.previewUrl",
        "boolean",
        "No",
        "Enable link previews. Defaults to false.",
      ],
    ],
  },
  {
    key: "image",
    label: "Image",
    description: "Send an image by an uploaded media ID or public HTTPS URL.",
    body: {
      to: "919876543210",
      message: {
        type: "image",
        media: { url: "https://cdn.example.com/product.jpg" },
        caption: "Here is the product you requested.",
      },
    },
    fields: [
      [
        "message.media",
        "object",
        "Yes",
        "Provide either media.id or an HTTPS media.url.",
      ],
      ["message.caption", "string", "No", "Caption shown with the image."],
    ],
  },
  {
    key: "video",
    label: "Video",
    description: "Send a video by an uploaded media ID or public HTTPS URL.",
    body: {
      to: "919876543210",
      message: {
        type: "video",
        media: { url: "https://cdn.example.com/demo.mp4" },
        caption: "A short product demonstration.",
      },
    },
    fields: [
      [
        "message.media",
        "object",
        "Yes",
        "Provide either media.id or an HTTPS media.url.",
      ],
      ["message.caption", "string", "No", "Caption shown with the video."],
    ],
  },
  {
    key: "audio",
    label: "Audio",
    description: "Send audio from an uploaded media ID or public HTTPS URL.",
    body: {
      to: "919876543210",
      message: {
        type: "audio",
        media: { id: "987654321012345" },
      },
    },
    fields: [
      [
        "message.media",
        "object",
        "Yes",
        "Provide either media.id or an HTTPS media.url.",
      ],
    ],
  },
  {
    key: "document",
    label: "Document",
    description: "Send a document with an optional caption and filename.",
    body: {
      to: "919876543210",
      message: {
        type: "document",
        media: { url: "https://cdn.example.com/invoice.pdf" },
        filename: "invoice-1048.pdf",
        caption: "Your invoice is attached.",
      },
    },
    fields: [
      [
        "message.media",
        "object",
        "Yes",
        "Provide either media.id or an HTTPS media.url.",
      ],
      ["message.filename", "string", "No", "Displayed document filename."],
      ["message.caption", "string", "No", "Caption shown with the document."],
    ],
  },
  {
    key: "sticker",
    label: "Sticker",
    description: "Send a sticker by an uploaded media ID or public HTTPS URL.",
    body: {
      to: "919876543210",
      message: {
        type: "sticker",
        media: { id: "987654321012345" },
      },
    },
    fields: [
      [
        "message.media",
        "object",
        "Yes",
        "Provide either media.id or an HTTPS media.url.",
      ],
    ],
  },
  {
    key: "location",
    label: "Location",
    description: "Share geographic coordinates with optional place details.",
    body: {
      to: "919876543210",
      message: {
        type: "location",
        latitude: 28.6139,
        longitude: 77.209,
        name: "Connaught Place",
        address: "New Delhi, Delhi, India",
      },
    },
    fields: [
      ["message.latitude", "number", "Yes", "Latitude from -90 to 90."],
      ["message.longitude", "number", "Yes", "Longitude from -180 to 180."],
      ["message.name", "string", "No", "Name of the location."],
      ["message.address", "string", "No", "Human-readable address."],
    ],
  },
  {
    key: "buttons",
    label: "Buttons",
    description: "Send up to three interactive reply buttons.",
    body: {
      to: "919876543210",
      message: {
        type: "buttons",
        body: "Would you like to confirm your appointment?",
        footer: "Reply using one of the options below",
        buttons: [
          { id: "confirm", title: "Confirm" },
          { id: "reschedule", title: "Reschedule" },
        ],
      },
    },
    fields: [
      ["message.body", "string", "Yes", "Non-empty message body."],
      ["message.footer", "string", "No", "Supporting footer text."],
      [
        "message.buttons",
        "array",
        "Yes",
        "1–3 buttons. Every button requires an id and title.",
      ],
    ],
  },
];

const errorResponses = [
  ["400", "INVALID_RECIPIENT", "The recipient is not a valid international number."],
  ["400", "MESSAGE_REQUIRED", "The message value is missing or is not an object."],
  ["400", "TEXT_REQUIRED", "A text message has an empty or missing message.text value."],
  ["400", "INVALID_PREVIEW_URL", "message.previewUrl was provided but is not a boolean."],
  ["400", "INVALID_MEDIA_URL", "The media URL is invalid or does not use HTTPS."],
  ["400", "MEDIA_REQUIRED", "Neither a media ID nor media URL was provided."],
  ["400", "INVALID_LOCATION", "Latitude or longitude is missing or out of range."],
  ["400", "BODY_REQUIRED", "An interactive buttons message has no body text."],
  ["400", "INVALID_BUTTONS", "Buttons are missing, invalid, or outside the 1–3 limit."],
  ["400", "UNSUPPORTED_MESSAGE_TYPE", "The requested message type is not supported."],
  ["400", "INVALID_MESSAGE_ID", "The status lookup ID is not a valid message ID."],
  ["401", "Unauthorized", "The Bearer token is missing, invalid, or expired."],
  ["404", "MESSAGE_NOT_FOUND", "No message belonging to this account matches the ID."],
  ["409", "SESSION_WINDOW_CLOSED", "No inbound message was received from this customer in the last 24 hours."],
  ["500", "Internal error", "The request could not be completed by the server."],
];

const CodeBlock = ({ children, language = "json", label }) => {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        border: "1px solid #334155",
        borderRadius: 2.5,
        bgcolor: "#111827",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1, bgcolor: "#1f2937", color: "#cbd5e1" }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}
        >
          {label || language}
        </Typography>
        <Tooltip title={copied ? "Copied" : "Copy code"}>
          <IconButton
            size="small"
            onClick={copyCode}
            aria-label="Copy code"
            sx={{ color: copied ? "#4ade80" : "#cbd5e1" }}
          >
            {copied ? (
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            ) : (
              <ContentCopyRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <SyntaxHighlighter
        language={language}
        style={materialDark}
        customStyle={{
          margin: 0,
          padding: "20px",
          background: "#111827",
          fontSize: "0.82rem",
          lineHeight: 1.65,
        }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </Box>
  );
};

const SectionTitle = ({ id, eyebrow, children, description }) => (
  <Box id={id} sx={{ scrollMarginTop: 24, mb: 2.5 }}>
    {eyebrow && (
      <Typography
        variant="overline"
        color="primary.main"
        sx={{ fontWeight: 800, letterSpacing: 1 }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      component="h2"
      sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 750, lineHeight: 1.25 }}
    >
      {children}
    </Typography>
    {description && (
      <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
        {description}
      </Typography>
    )}
  </Box>
);

const ParameterTable = ({ rows }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none" }}>
    <Table sx={{ minWidth: 720 }}>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Description</TableCell>
          {rows[0]?.example !== undefined && <TableCell>Example</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const values = Array.isArray(row)
            ? { name: row[0], type: row[1], required: row[2], description: row[3] }
            : row;

          return (
            <TableRow key={values.name}>
              <TableCell>
                <Box
                  component="code"
                  sx={{
                    color: "primary.dark",
                    bgcolor: "rgba(18, 140, 126, 0.08)",
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.35,
                    fontSize: "0.78rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {values.name}
                </Box>
              </TableCell>
              <TableCell>{values.type}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={values.required === true || values.required === "Yes" ? "Required" : "Optional"}
                  color={values.required === true || values.required === "Yes" ? "primary" : "default"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{values.description}</TableCell>
              {rows[0]?.example !== undefined && (
                <TableCell>
                  <Box component="code" sx={{ fontSize: "0.78rem" }}>
                    {values.example}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const SessionApiDocs = () => {
  const [messageType, setMessageType] = useState("text");
  const [codeLanguage, setCodeLanguage] = useState("curl");
  const { data: apiCred, isLoading, isError, error } = useApiEp(
    `/user/get-api-cred`
  );

  const sessionEndpoint =
    apiCred?.endpoint?.session || "https://api.example.com/send/session";
  const statusEndpoint = useMemo(() => {
    if (/\/session\/?$/.test(sessionEndpoint)) {
      return sessionEndpoint.replace(/\/session\/?$/, "/message-status/:id");
    }
    return `${sessionEndpoint.replace(/\/$/, "")}/message-status/:id`;
  }, [sessionEndpoint]);

  const selectedMessage =
    messageTypes.find((type) => type.key === messageType) || messageTypes[0];
  const requestBody = JSON.stringify(selectedMessage.body, null, 2);

  const codeSamples = {
    curl: `curl --request POST '${sessionEndpoint}' \\
  --header 'Authorization: Bearer YOUR_API_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '${requestBody}'`,
    javascript: `const response = await fetch("${sessionEndpoint}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "Content-Type": "application/json"
  },
  body: JSON.stringify(${requestBody})
});

const result = await response.json();

if (!response.ok) {
  throw new Error(result.error?.message || "Request failed");
}

console.log(result.data);`,
    node: `import axios from "axios";

const { data } = await axios.post(
  "${sessionEndpoint}",
  ${requestBody},
  {
    headers: {
      Authorization: "Bearer YOUR_API_TOKEN",
      "Content-Type": "application/json"
    }
  }
);

console.log(data.data);`,
  };

  const successResponse = `{
  "success": true,
  "data": {
    "id": "66f15c2dc06e9b0012345678",
    "messageId": "wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgS...",
    "status": "accepted",
    "to": "919876543210",
    "type": "${selectedMessage.key}",
    "clientReference": "support-1048"
  }
}`;

  const sidebarLinks = [
    ["overview", "Overview"],
    ["authentication", "Authentication"],
    ["send-message", "Send a message"],
    ["message-types", "Message types"],
    ["response", "Response"],
    ["message-status", "Message status"],
    ["errors", "Errors"],
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      <BackBtn bstyle={{ mb: 1.5 }} />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 3, md: 5 },
          mb: 4,
          borderRadius: 3.5,
          color: "common.white",
          background:
            "linear-gradient(120deg, #063d38 0%, #075e54 52%, #128c7e 100%)",
          boxShadow: "0 18px 48px rgba(7, 94, 84, 0.22)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 320,
            height: 320,
            right: -90,
            top: -180,
            borderRadius: "50%",
            border: "52px solid rgba(255,255,255,0.06)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 820 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label="REST API"
              size="small"
              sx={{ color: "white", bgcolor: "rgba(255,255,255,0.14)" }}
            />
            <Chip
              label="v1"
              size="small"
              sx={{ color: "white", bgcolor: "rgba(255,255,255,0.14)" }}
            />
          </Stack>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: 30, sm: 42 }, fontWeight: 780, lineHeight: 1.12 }}
          >
            Session Messaging API
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: { xs: 16, sm: 18 }, opacity: 0.82 }}>
            Send text, media, location, and interactive WhatsApp messages during
            an active customer-service conversation.
          </Typography>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error?.message || "Could not load your API endpoint."} Examples are
          showing a placeholder URL.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "220px minmax(0, 1fr)" },
          gap: { xs: 3, lg: 5 },
          alignItems: "start",
        }}
      >
        <Box
          component="nav"
          aria-label="On this page"
          sx={{
            display: { xs: "none", lg: "block" },
            position: "sticky",
            top: 24,
            borderLeft: "1px solid",
            borderColor: "divider",
            pl: 2,
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>
            On this page
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            {sidebarLinks.map(([id, label]) => (
              <Link
                key={id}
                href={`#${id}`}
                underline="none"
                color="text.secondary"
                sx={{
                  py: 0.75,
                  fontSize: "0.875rem",
                  "&:hover": { color: "primary.main" },
                }}
              >
                {label}
              </Link>
            ))}
          </Stack>
        </Box>

        <Stack spacing={6} sx={{ minWidth: 0 }}>
          <Box>
            <SectionTitle
              id="overview"
              eyebrow="Introduction"
              description="This API is for free-form messages sent after a customer has contacted your WhatsApp number. For proactive messages or conversations outside this window, use an approved template message."
            >
              Overview
            </SectionTitle>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {[
                [<ScheduleRoundedIcon />, "24-hour window", "An inbound customer message must exist within the last 24 hours."],
                [<SendRoundedIcon />, "8 message types", "Text, image, video, audio, document, sticker, location, and buttons."],
                [<TrackChangesRoundedIcon />, "Trackable", "Use the returned ID to retrieve the latest delivery status."],
              ].map(([icon, title, text]) => (
                <Card key={title} elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                  <Box sx={{ color: "primary.main", mb: 1.25 }}>{icon}</Box>
                  <Typography fontWeight={700}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {text}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>

          <Box>
            <SectionTitle
              id="authentication"
              eyebrow="Security"
              description="Every request must include your API token as a Bearer token. You can create or rotate it from API settings."
            >
              Authentication
            </SectionTitle>
            <Alert
              icon={<KeyRoundedIcon />}
              severity="info"
              sx={{ mb: 2.5, borderRadius: 2 }}
            >
              Keep this token on your server. Never expose it in browser code,
              mobile apps, or public repositories.
            </Alert>
            <CodeBlock language="http" label="Authorization header">
              {`Authorization: Bearer YOUR_API_TOKEN`}
            </CodeBlock>
          </Box>

          <Box>
            <SectionTitle
              id="send-message"
              eyebrow="Endpoint"
              description="Send a message to one recipient during an open customer-service window."
            >
              Send a session message
            </SectionTitle>

            <Card elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
              >
                <Chip
                  label="POST"
                  color="primary"
                  sx={{ fontWeight: 800, alignSelf: "flex-start" }}
                />
                {isLoading ? (
                  <Skeleton width="70%" height={32} />
                ) : (
                  <Box
                    component="code"
                    sx={{
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      color: "text.primary",
                    }}
                  >
                    {sessionEndpoint}
                  </Box>
                )}
              </Stack>
            </Card>

            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              Request body
            </Typography>
            <ParameterTable rows={requestParameters} />
          </Box>

          <Box>
            <SectionTitle
              id="message-types"
              eyebrow="Examples"
              description="Choose a message type to see its payload, fields, and ready-to-use request examples."
            >
              Message types
            </SectionTitle>

            <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}>
              <Tabs
                value={messageType}
                onChange={(_, value) => setMessageType(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Message types"
              >
                {messageTypes.map((type) => (
                  <Tab key={type.key} value={type.key} label={type.label} />
                ))}
              </Tabs>
            </Box>

            <Typography variant="h6" fontWeight={700}>
              {selectedMessage.label} message
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              {selectedMessage.description}
            </Typography>

            <ParameterTable rows={selectedMessage.fields} />

            <Box sx={{ mt: 3 }}>
              <Tabs
                value={codeLanguage}
                onChange={(_, value) => setCodeLanguage(value)}
                sx={{ minHeight: 42, mb: 1 }}
              >
                <Tab value="curl" label="cURL" sx={{ minHeight: 42 }} />
                <Tab value="javascript" label="JavaScript" sx={{ minHeight: 42 }} />
                <Tab value="node" label="Node.js" sx={{ minHeight: 42 }} />
              </Tabs>
              <CodeBlock
                language={codeLanguage === "curl" ? "bash" : "javascript"}
                label={codeLanguage === "curl" ? "cURL" : codeLanguage === "node" ? "Node.js + Axios" : "JavaScript + Fetch"}
              >
                {codeSamples[codeLanguage]}
              </CodeBlock>
            </Box>

            {["image", "video", "audio", "document", "sticker"].includes(
              messageType
            ) && (
              <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
                Use either <code>message.media.id</code> or{" "}
                <code>message.media.url</code>. URL-based media must use HTTPS.
              </Alert>
            )}
          </Box>

          <Box>
            <SectionTitle
              id="response"
              eyebrow="HTTP 202"
              description="A successful response means WhatsApp accepted the message for processing. It does not guarantee final delivery."
            >
              Send response
            </SectionTitle>
            <CodeBlock language="json" label="202 Accepted">
              {successResponse}
            </CodeBlock>
            <Stack spacing={1.25} sx={{ mt: 2.5 }}>
              {[
                ["id", "Internal message ID. Use this value with the status endpoint."],
                ["messageId", "WhatsApp message ID returned by Meta."],
                ["status", "Initial processing state, normally accepted."],
                ["clientReference", "Your correlation ID, when supplied in the request."],
              ].map(([field, description]) => (
                <Stack key={field} direction="row" spacing={1.5} alignItems="baseline">
                  <Box component="code" sx={{ minWidth: 112, fontWeight: 700, color: "primary.dark" }}>
                    {field}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {description}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box>
            <SectionTitle
              id="message-status"
              eyebrow="Endpoint"
              description="Retrieve the latest stored state of a message using the internal id from the send response."
            >
              Get message status
            </SectionTitle>
            <Card elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 3 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                <Chip label="GET" color="info" sx={{ fontWeight: 800, alignSelf: "flex-start" }} />
                {isLoading ? (
                  <Skeleton width="70%" height={32} />
                ) : (
                  <Box component="code" sx={{ minWidth: 0, overflowWrap: "anywhere", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                    {statusEndpoint}
                  </Box>
                )}
              </Stack>
            </Card>
            <CodeBlock language="bash" label="cURL">
              {`curl --request GET '${statusEndpoint.replace(":id", "66f15c2dc06e9b0012345678")}' \\
  --header 'Authorization: Bearer YOUR_API_TOKEN'`}
            </CodeBlock>
            <Box sx={{ mt: 2.5 }}>
              <CodeBlock language="json" label="200 OK">
                {`{
  "success": true,
  "data": {
    "id": "66f15c2dc06e9b0012345678",
    "messageId": "wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgS...",
    "to": "919876543210",
    "type": "text",
    "status": "delivered",
    "clientReference": "support-1048",
    "createdAt": "2026-09-16T08:30:00.000Z",
    "updatedAt": "2026-09-16T08:30:04.000Z"
  }
}`}
              </CodeBlock>
            </Box>
          </Box>

          <Box>
            <SectionTitle
              id="errors"
              eyebrow="Troubleshooting"
              description="Validation errors use a consistent error object. Authentication errors return a message field instead."
            >
              Errors
            </SectionTitle>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              alignItems="stretch"
              sx={{ mb: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CodeBlock language="json" label="Validation error">
                  {`{
  "success": false,
  "error": {
    "code": "INVALID_RECIPIENT",
    "message": "to must be a valid international phone number including country code."
  }
}`}
                </CodeBlock>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CodeBlock language="json" label="Session window closed">
                  {`{
  "success": false,
  "error": {
    "code": "SESSION_WINDOW_CLOSED",
    "message": "The customer-service window is closed. Send an approved template message instead."
  }
}`}
                </CodeBlock>
              </Box>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none" }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Meaning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorResponses.map(([status, code, meaning]) => (
                    <TableRow key={`${status}-${code}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          color={status === "409" ? "warning" : status === "401" ? "default" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box component="code" sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                          {code}
                        </Box>
                      </TableCell>
                      <TableCell>{meaning}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Card
            elevation={0}
            sx={{
              ...cardSx,
              p: { xs: 2.5, sm: 3 },
              bgcolor: "rgba(255, 171, 0, 0.055)",
              borderColor: "rgba(255, 171, 0, 0.35)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <WarningAmberRoundedIcon color="warning" />
              <Box>
                <Typography fontWeight={750}>Before going live</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Handle non-2xx responses, store the returned internal message ID,
                  avoid logging API tokens, and fall back to an approved template
                  when the 24-hour customer-service window is closed.
                </Typography>
              </Box>
            </Stack>
          </Card>

          <Divider />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={1}
            sx={{ pb: 3 }}
          >
            <Typography variant="body2" color="text.secondary">
              Session Messaging API · REST · JSON
            </Typography>
            <Link href="#overview" underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              Back to top <ArrowForwardRoundedIcon sx={{ transform: "rotate(-90deg)", fontSize: 18 }} />
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default SessionApiDocs;
